/**
 * Single source of truth for every enum in the system (spec §6.0).
 * Mongoose schemas and Zod schemas both import from here — never re-type a
 * string-literal union in two places.
 */
export const SYSTEM_ROLES = ['owner', 'admin', 'manager', 'member', 'guest'];
export const PROJECT_ROLES = ['lead', 'contributor', 'reviewer', 'viewer'];
export const DESIGNATIONS = [
    'founder',
    'team_lead',
    'sr_developer',
    'developer',
    'designer',
    'qa',
    'devops',
    'marketing',
    'content',
    'accounts',
    'intern',
    'other',
];
export const TASK_TYPES = [
    'feature',
    'bug',
    'design',
    'content',
    'chore',
    'research',
    'spike',
    'incident',
    'test',
    'release',
    'campaign',
];
export const PRIORITIES = ['P0', 'P1', 'P2', 'P3'];
export const STATUS_CATEGORIES = ['todo', 'in_progress', 'done', 'cancelled'];
export const PROJECT_STATUSES = [
    'planning',
    'active',
    'on_hold',
    'completed',
    'archived',
];
export const PROJECT_VISIBILITIES = ['workspace', 'private', 'guest_shared'];
export const USER_STATUSES = ['invited', 'active', 'suspended'];
export const NOTE_SCOPES = ['personal', 'task', 'project', 'meeting'];
export const COMMENT_ENTITY_TYPES = ['task', 'note'];
// `chat_message` carries the *conversation* id: a composer attachment is
// uploaded before its message exists, so the conversation is what owns it and
// what membership is checked against (V5-01 §5).
export const ATTACHMENT_ENTITY_TYPES = [
    'task',
    'comment',
    'note',
    'project',
    'user',
    'chat_message',
];
export const UPLOAD_STATUSES = ['pending', 'complete', 'failed'];
export const TIME_ENTRY_SOURCES = ['timer', 'manual'];
export const APPROVAL_STATUSES = ['draft', 'submitted', 'approved', 'rejected'];
export const SPRINT_STATUSES = ['planned', 'active', 'completed'];
export const LEAVE_TYPES = ['leave', 'half_day', 'wfh'];
export const LEAVE_STATUSES = [
    'pending',
    'approved',
    'rejected',
    'cancelled',
    'revoked',
];
export const RECURRENCE_FREQS = ['daily', 'weekly', 'monthly', 'yearly'];
export const CUSTOM_FIELD_TYPES = [
    'text',
    'number',
    'select',
    'multiselect',
    'date',
    'checkbox',
    'url',
    'user',
    'currency',
];
export const VIEW_TYPES = ['list', 'board', 'calendar', 'timeline', 'workload'];
export const SAVED_VIEW_SCOPES = ['private', 'project', 'workspace'];
export const TEMPLATE_KINDS = ['task', 'project'];
export const NOTIFICATION_TYPES = [
    'task.assigned',
    'task.unassigned',
    'task.mentioned',
    'task.comment',
    'task.status_changed',
    'task.due_soon',
    'task.overdue',
    'task.blocked',
    'task.unblocked',
    'task.review_requested',
    'task.review_approved',
    'task.review_rejected',
    'task.reported_completed',
    'project.added',
    'sprint.started',
    'sprint.closed',
    'automation.fired',
    'leave.requested',
    'leave.approved',
    'leave.rejected',
    'digest.founder',
    'digest.member',
    'digest.lead_weekly',
    // V5-01 §7 — the Chat row in the preference matrix. Its own key so that
    // silencing task comments does not also silence colleagues.
    'chat.message',
    // V5-03 §2 — a Calendly booking landing in someone's calendar. Read-only
    // data about their day, so it is a notification and never a task.
    'calendar.booking',
    // V6-04 §4 — the two attendance nudges (start not recorded, session still
    // open at the expected end), and the correction request/decision. Separate
    // keys because they are aimed at different people: a reminder is yours, a
    // correction is an approver's.
    'attendance.reminder',
    'attendance.correction',
    'admin.alert',
    'export.ready',
];
/* ── Calendar (V5-03) ──────────────────────────────────── */
/**
 * Where an external calendar row came from.
 *
 * Calendly was the only member in v1, and the note left here then said the
 * enum existed so that Phase B — Google and Microsoft two-way sync — would be
 * an additive change rather than a migration over everything ever synced.
 * V6-07 is that phase, and it was: two members appended, no backfill, every
 * existing row still saying `calendly` and still meaning it.
 */
export const CALENDAR_EVENT_SOURCES = ['calendly', 'google', 'microsoft'];
export const CALENDAR_EVENT_STATUSES = ['confirmed', 'cancelled'];
/** What happened to one inbound webhook, for the deliveries list (§2). */
export const WEBHOOK_OUTCOMES = [
    'matched',
    'ignored_no_member',
    'ignored_duplicate',
    'ignored_unhandled_event',
    'rejected_bad_signature',
    'rejected_malformed',
];
/**
 * The layers a user can switch on and off on the calendar (V5-03 §3).
 *
 * V6-07 §4 adds a per-source row underneath `meetings`. `meetings` stays the
 * master switch — turning it off hides every meeting whatever its origin — and
 * the three source keys decide which of the visible ones are drawn. Separate
 * keys rather than a nested object because the whole preference is one array of
 * strings on the user document, and a nested shape there would need a migration
 * to add the next provider.
 */
export const CALENDAR_LAYERS = [
    'tasks',
    'leave',
    'holidays',
    'meetings',
    'meetings_calendly',
    'meetings_google',
    'meetings_microsoft',
];
/** The layer key that governs one meeting source — V6-07 §4. */
export const MEETING_SOURCE_LAYERS = {
    calendly: 'meetings_calendly',
    google: 'meetings_google',
    microsoft: 'meetings_microsoft',
};
/* ── Two-way calendar sync (V6-07) ─────────────────────── */
/** The providers VirtueTasks can hold an OAuth connection with (§2). */
export const CALENDAR_PROVIDERS = ['google', 'microsoft'];
/**
 * The health of one person's connection to one provider (§2).
 *
 * `needs_reauth` is a first-class state rather than an error string because it
 * is the only one with an action attached: the tokens are dead, no retry will
 * revive them, and the person has to consent again. A connection that fails
 * silently and stays "connected" is the failure mode §2 exists to prevent.
 */
export const CALENDAR_CONNECTION_STATUSES = ['connected', 'needs_reauth', 'disconnected'];
/**
 * One line in the connection's own log — the `notificationdeliveries`-style
 * rows §4 asks for, visible in `/platform` for debugging.
 *
 * Every outbound push and every inbound pull records one, successful or not,
 * because "calendar sync is quiet" has to be answerable with a row rather than
 * a shrug (protocol §4).
 */
export const CALENDAR_SYNC_EVENTS = [
    'connected',
    'reconnected',
    'needs_reauth',
    'disconnected',
    'initial_sync',
    'incremental_sync',
    'fallback_poll',
    'outbound_push',
    'inbound_apply',
    'channel_renewed',
    'webhook_received',
    'webhook_rejected',
    'test_sync',
];
export const CALENDAR_SYNC_OUTCOMES = ['ok', 'skipped', 'failed'];
export const NOTIFICATION_CHANNELS = ['inApp', 'email', 'whatsapp', 'push'];
export const DELIVERY_CHANNELS = ['email', 'whatsapp', 'push'];
export const DELIVERY_STATUSES = ['queued', 'sent', 'delivered', 'failed', 'skipped'];
export const DELIVERY_SKIP_REASONS = [
    'quiet_hours',
    'preference_off',
    'no_phone',
    'phone_unverified',
    'channel_disabled',
    'user_inactive',
    /**
     * Neither the workspace nor (where permitted) the platform had a key for
     * this channel. Recorded rather than dropped so it surfaces on Settings →
     * Integrations instead of looking like a quiet week.
     */
    'channel_not_configured',
    /**
     * The workspace has no key of its own AND a platform admin has switched
     * `plan.allowPlatformChannels` off, so the platform's key is not available
     * to it (V5-05 §2.4).
     *
     * Split out from `channel_not_configured` because the two need opposite
     * fixes — one is "add your key", the other is "ask the platform to turn
     * this back on" — and collapsing them is what made the original outage so
     * hard to read: the workspace looked unconfigured when it was disallowed.
     */
    'platform_channels_disabled',
    /**
     * The workspace has used its monthly allowance on the platform's credentials
     * (V5-07 §3). Recorded rather than dropped, like every other skip, so the
     * Usage screen and the Deliveries panel agree about why it went quiet.
     *
     * Never applies to auth-critical mail: a customer at 100% can still get
     * people logged in.
     */
    'quota_exceeded',
    /* ── Browser push (V6-01 §2) ─────────────────────────── */
    /**
     * The person has never enabled push on any browser. Distinct from
     * `preference_off`: one is answered by clicking "Enable on this device",
     * the other by ticking a box in the matrix, and an admin looking at a quiet
     * channel needs to know which.
     */
    'not_subscribed',
    /**
     * They were connected when the event fired, so the in-app notification
     * handled it (§3). Not a failure — the row exists so "I got no push" has an
     * answer rather than a shrug.
     */
    'user_active',
    /**
     * The browser's push service answered 404/410: the subscription is dead and
     * the row was deleted. Recorded because a device silently disappearing from
     * someone's list otherwise looks like a bug in the list.
     */
    'endpoint_gone',
];
export const SESSION_REVOKE_REASONS = [
    'logout',
    'rotation',
    'reuse_detected',
    'admin',
    'password_change',
];
export const INVITATION_STATUSES = ['pending', 'accepted', 'expired', 'revoked'];
export const AUTOMATION_TRIGGERS = [
    'task.created',
    'task.status_changed',
    'task.priority_changed',
    'task.assignee_changed',
    'task.due_arrives',
    'task.overdue_by_days',
    'task.comment_added',
    'task.checklist_completed',
    'task.subtasks_done',
    'task.field_changed',
    'schedule.cron',
];
export const AUTOMATION_OPERATORS = [
    'equals',
    'notEquals',
    'in',
    'contains',
    'gt',
    'lt',
    'isEmpty',
    'isSet',
];
export const AUTOMATION_ACTIONS = [
    'set_status',
    'set_priority',
    'assign_user',
    'assign_team_round_robin',
    'add_label',
    'remove_label',
    'set_due_date',
    'add_checklist_from_template',
    'add_comment',
    'create_linked_task',
    'notify',
    'move_to_sprint',
    'archive',
];
export const AUTOMATION_RUN_STATUSES = ['success', 'failed', 'skipped'];
export const DESIGNATION_PROFILES = {
    founder: { capacityHoursPerWeek: 10, taskTypes: 'any', defaultView: '/dashboard' },
    team_lead: {
        capacityHoursPerWeek: 25,
        taskTypes: ['feature', 'bug', 'chore', 'research'],
        defaultView: '/workload',
    },
    sr_developer: {
        capacityHoursPerWeek: 32,
        taskTypes: ['feature', 'bug', 'chore', 'spike'],
        defaultView: '/my-day',
    },
    developer: {
        capacityHoursPerWeek: 35,
        taskTypes: ['feature', 'bug', 'chore'],
        defaultView: '/my-day',
    },
    designer: { capacityHoursPerWeek: 35, taskTypes: ['design', 'research'], defaultView: '/projects' },
    qa: { capacityHoursPerWeek: 35, taskTypes: ['bug', 'test'], defaultView: '/projects' },
    devops: { capacityHoursPerWeek: 32, taskTypes: ['chore', 'incident'], defaultView: '/projects' },
    marketing: {
        capacityHoursPerWeek: 35,
        taskTypes: ['content', 'campaign', 'chore'],
        defaultView: '/my-day',
    },
    content: { capacityHoursPerWeek: 35, taskTypes: ['content'], defaultView: '/my-day' },
    accounts: { capacityHoursPerWeek: 20, taskTypes: ['chore'], defaultView: '/my-tasks' },
    intern: { capacityHoursPerWeek: 30, taskTypes: ['chore', 'bug'], defaultView: '/my-day' },
    other: { capacityHoursPerWeek: 35, taskTypes: 'any', defaultView: '/my-day' },
};
/** Priority base scores — spec §4.4 */
export const PRIORITY_BASE_SCORE = {
    P0: 1000,
    P1: 600,
    P2: 300,
    P3: 100,
};
/** Design tokens shared with the UI — spec §8.3 */
export const PRIORITY_COLOURS = {
    P0: '#C2413A',
    P1: '#C9762B',
    P2: '#4A7CB5',
    P3: '#7C838A',
};
export const STATUS_CATEGORY_COLOURS = {
    todo: '#7C838A',
    in_progress: '#C9762B',
    done: '#2F6F4E',
    cancelled: '#9BA1A8',
};
export const DEFAULT_STATUSES = [
    { name: 'Backlog', category: 'todo', colour: '#64748B' },
    { name: 'To Do', category: 'todo', colour: '#3B82F6' },
    { name: 'In Progress', category: 'in_progress', colour: '#F59E0B', wipLimit: 3 },
    { name: 'In Review', category: 'in_progress', colour: '#8B5CF6' },
    { name: 'In QA', category: 'in_progress', colour: '#06B6D4', qaOnly: true },
    { name: 'Blocked', category: 'in_progress', colour: '#EF4444', requiresReason: true },
    { name: 'Done', category: 'done', colour: '#22C55E' },
    { name: 'Cancelled', category: 'cancelled', colour: '#71717A', requiresReason: true },
];
export const MAX_ASSIGNEES = 5;
export const MAX_TASK_DEPTH = 2;
export const MAX_BULK_TASKS = 200;
export const MAX_QUERY_LIMIT = 200;
export const MAX_CUSTOM_FIELDS_PER_PROJECT = 15;
export const MAX_AUTOMATION_CONDITIONS = 10;
export const MAX_AUTOMATION_CHAIN_DEPTH = 5;
export const MAX_DEPENDENCY_DEPTH = 20;
export const NOTE_VERSION_KEEP = 50;
export const RECOVERY_CODE_COUNT = 10;
/* ── Plan tiers and quotas — V5-07 ─────────────────────── */
/**
 * Plan tiers. `custom` exists so a platform admin can set numbers that match a
 * contract without inventing a tier for one customer.
 */
export const PLAN_TIERS = ['free', 'starter', 'team', 'business', 'custom'];
/**
 * Defaults per tier (V5-07 §1). Editable per workspace in `/platform` — these
 * are the starting point, not a ceiling.
 */
export const PLAN_QUOTA_DEFAULTS = {
    free: { storageMB: 1024, emailsPerMonth: 500, whatsappPerMonth: 100 },
    starter: { storageMB: 10240, emailsPerMonth: 5000, whatsappPerMonth: 500 },
    team: { storageMB: 51200, emailsPerMonth: 20000, whatsappPerMonth: 2000 },
    business: { storageMB: 204800, emailsPerMonth: 75000, whatsappPerMonth: 7500 },
};
export const DEFAULT_PLAN_QUOTAS = PLAN_QUOTA_DEFAULTS.free;
/** The fraction at which a workspace is warned it is running out (V5-07 §3). */
export const QUOTA_WARN_AT = 0.8;
/** Which quotas exist — the loop variable for warnings and the usage screen. */
export const QUOTA_KINDS = ['storage', 'email', 'whatsapp'];
/** `YYYY-MM` for a date, in UTC — the key of a usage period. */
export function usagePeriod(at = new Date()) {
    return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}`;
}
//# sourceMappingURL=enums.js.map